export const prefix = "/api/v1"; // import.meta.env.VITE_API_URL;
const enableDebug = import.meta.env.VITE_IS_DEBUG;

import { store } from "./store";

import { navigate } from "wouter/use-browser-location";
import { notifications } from "@mantine/notifications";
import { slugify } from "../../Boilerplate/_Shared/slugify";
import mixpanel from "mixpanel-browser";
// import mixpanel from "mixpanel-browser";

const jsonHeaders = {
  "content-type": "application/json",
  accept: "application/json",
};

function getAuthHeaders() {
  const authToken = store.getState().authToken;
  if (authToken) return { authorization: `Bearer ${authToken}` };
  return {};
}

// Runs a request that streams JSON objects, invoking callback for each object
export async function jsonApiRequestStreaming(method, uri, values, callback) {
  const authHeader = getAuthHeaders();

  var reqDetails = {
    url: `${prefix}/${uri}`,
    method: method,
    headers: {
      ...jsonHeaders,
      ...authHeader,
    },
    body: values ? JSON.stringify(values) : null,
  };

  if (enableDebug) console.debug("executing STREAMING request", reqDetails);

  const response = await fetch(reqDetails.url, {
    method: reqDetails.method,
    headers: reqDetails.headers,
    body: reqDetails.body,
  });

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");

    buffer = lines.pop(); // Keep the last incomplete line in the buffer

    for (const line of lines) {
      if (line.trim() !== "") {
        try {
          if (enableDebug) console.debug("Received STREAMING line:", line);
          const json = JSON.parse(line);
          if (enableDebug) console.log("Received STREAMING JSON object:", json);

          callback(json);
        } catch (error) {
          console.error("Error parsing JSON:", error);
        }
      }
    }
  }

  // Process any remaining data in the buffer
  if (buffer.trim() !== "") {
    try {
      const json = JSON.parse(buffer);
      if (enableDebug) console.log("Received FINAL JSON object:", json);
      callback(json);
    } catch (error) {
      console.error("Error parsing JSON:", error);
    }
  }
}

async function showRequestError(response) {
  let responseText = await response.text();
  let message = `${response.statusText} (${response.status}): ${responseText}`;

  if (enableDebug) console.debug("response text", responseText);

  let responseJson = null;
  try {
    responseJson = JSON.parse(responseText);
    if (enableDebug) console.debug("responseJson", responseJson);
  } catch (parseError) {
    if (enableDebug)
      console.debug("Failed to parse response as JSON", parseError);
  }

  if (responseJson) {
    notifications.show({
      color: "red",
      autoClose: 10_000,
      title: responseJson.title || "An error occurred!",
      message: responseJson.detail || responseText,
    });
  } else {
    notifications.show({
      color: "red",
      autoClose: 5000,
      title: "An error occurred!",
      message: message,
    });
  }

  return message;
}

export async function apiRequest(method, uri, values) {
  const authHeader = getAuthHeaders();

  var reqDetails = {
    url: `${prefix}/${uri}`,
    method: method,
    headers: {
      ...jsonHeaders,
      ...authHeader,
    },
    body: values ? JSON.stringify(values) : null,
  };

  const response = await fetch(reqDetails.url, {
    method: reqDetails.method,
    headers: reqDetails.headers,
    body: reqDetails.body,
  });

  if (response.status === 401 && authHeader.authorization) {
    console.warn("Unauthorized request, logging out user");
    userLogout(true);
    return null;
  }

  if (!response.ok) {
    const message = await showRequestError(response);
    throw new Error(message);
  }

  return response;
}

export async function jsonApiRequest(method, uri, values) {
  const response = await apiRequest(method, uri, values);
  if (!response) return null;

  if (response.status === 204) return {};
  try {
    // parse the JSON response
    const json = await response.json();
    return json;
  } catch (error) {
    console.error("Error parsing JSON:", error);
    return {};
  }
}

export async function jsonPost(uri, values) {
  return await jsonApiRequest("POST", uri, values);
}

export async function jsonGet(uri) {
  return await jsonApiRequest("GET", uri, null);
}

export async function jsonPut(uri, values) {
  return await jsonApiRequest("PUT", uri, values);
}

export async function jsonDelete(uri) {
  return await jsonApiRequest("DELETE", uri, null);
}

// TBD -- not really an API call... should move into store
export const userLogout = (isInvoluntary = false) => {
  store.getState().setAuthToken(null);
  store.getState().setProfile(null);
  navigate("/", { replace: true });
  if (isInvoluntary) {
    // TBD: debounce this
    try {
      mixpanel.track("User logged out involuntarily");
    } catch {}
    notifications.show({
      color: "red",
      autoClose: 10_000,
      title: "You are logged out",
      message: "Please login again",
    });
  }
};

export const updateProduct = async (id, dto) =>
  await jsonPut(`products/${id}`, dto);

export const getProducts = async (
  page = 1,
  pageSize = 25,
  mappingId = null,
  importId = null
) => {
  const params = new URLSearchParams();

  if (page) params.append("page", page);
  if (pageSize) params.append("pageSize", pageSize);
  if (mappingId) params.append("mappingId", mappingId);
  if (importId) params.append("importId", importId);

  const queryString = params.toString();
  const url = `products${queryString ? `?${queryString}` : ""}`;

  return await jsonGet(url);
};

export const getCatalogueDefaults = async () =>
  await jsonGet("catalogue-defaults");

export const getGuaranteeOptions = async (mappingId) =>
  await jsonGet(`catalogue-defaults/mappings/${mappingId}/guarantee-options`);

export const searchBrandOptions = async (mappingId, query = "") =>
  await jsonGet(
    `catalogue-defaults/mappings/${mappingId}/brand-options?q=${encodeURIComponent(query)}`
  );

export const saveCatalogueDefaults = async (dto) =>
  await jsonPut("catalogue-defaults", dto);

export const applyCatalogueDefaults = async (mappingId, dto) =>
  await jsonPost(`catalogue-defaults/mappings/${mappingId}/apply`, dto);

export const getMappingDetails = async (mappingId) =>
  await jsonGet(`mappings/${mappingId}`);

export const getProduct = async (id) => await jsonGet(`products/${id}`);
export const getProductStatus = async (id) =>
  await jsonGet(`products/${id}/status`);
export const getVariantGroupOptions = async (id) =>
  await jsonGet(`products/${id}/variant-group-options`);

// export const getProductTypesPaged = async (marketplaceId, page = 1, pageSize = 25) =>
//   await jsonGet(`marketplaces/${marketplaceId}/types?page=${page}&pageSize=${pageSize}`);

export const getProductTypesAll = async (marketplaceId) =>
  await jsonGet(`marketplaces/${marketplaceId}/types/all`);

export const getProductTypesTree = async (marketplaceId, root = null) =>
  await jsonGet(`marketplaces/${marketplaceId}/types/tree?root=${root}`);

export const getProductType = async (marketplaceId, id) =>
  await jsonGet(`marketplaces/${marketplaceId}/types/${id}`);

export const getMarketplaces = async () => await jsonGet(`marketplaces`);

export const getMarketplace = async (id) =>
  await jsonGet(`admin/marketplaces/${id}`);
export const updateMarketplace = async (id, dto) =>
  await jsonPut(`admin/marketplaces/${id}`, dto);
export const createMarketplace = async (dto) =>
  await jsonPost(`admin/marketplaces`, dto);
export const triggerMarketplaceRefresh = async (id) =>
  await jsonPost(`admin/marketplaces/${id}/bulk-update`);

export const acceptCatalogGeneration = async (id, generationId) =>
  await jsonPost(
    `admin/marketplaces/${id}/catalog-generations/${generationId}/accept`
  );

export const discardCatalogGeneration = async (id, generationId) =>
  await jsonPost(
    `admin/marketplaces/${id}/catalog-generations/${generationId}/discard`
  );

export const revertCatalogGeneration = async (id) =>
  await jsonPost(`admin/marketplaces/${id}/catalog/revert`);

export const getAbstractMapping = async (marketplaceId) =>
  await jsonGet(`marketplaces/${marketplaceId}/mapping`);

export const updateAbstractMapping = async (marketplaceId, dto) =>
  await jsonPut(`marketplaces/${marketplaceId}/mapping`, dto);

export const getAdminJobs = async (
  page,
  pageSize,
  tenantFilter,
  statusFilter
) =>
  await jsonGet(
    `admin-jobs?page=${page}&pageSize=${pageSize}&tenantFilter=${tenantFilter}&statusFilter=${statusFilter}`
  );

function encodeFileName(fileName) {
  // https://stackoverflow.com/questions/44429173/javascript-encodeuri-failed-to-encode-round-bracket
  return encodeURIComponent(fileName).replace(/[!'()*]/g, function (c) {
    return "%" + c.charCodeAt(0).toString(16);
  });
}

export async function uploadFile(file, type = null, marketplaceId = null) {
  let authHeaders = getAuthHeaders();

  let simpleFileName = slugify(file.name);
  let uriFileName = encodeFileName(file.name);

  const uri = type
    ? `${prefix}/imports/${type}/${marketplaceId ?? ""}`
    : `${prefix}/imports`;

  var response = await fetch(uri, {
      method: "POST",
      body: file,
      headers: {
        ...authHeaders,
        "content-type": file.type,
        "content-length": `${file.size}`,
        "content-disposition": `attachment; filename=\"${simpleFileName}\"; filename*=UTF-8''${uriFileName}`,
      },
    }
  );

  if (!response.ok) {
    const message = await showRequestError(response);
    throw new Error(message);
  }
  return await response.json();
}

async function downloadResponseAsFile(response) {
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const contentDisposition = response.headers.get("Content-Disposition");
  const filenameMatch = contentDisposition?.match(
    /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
  );
  const filename = filenameMatch
    ? filenameMatch[1].replace(/['"]/g, "")
    : "download";
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

export async function createExport(mappingId, dto) {
  const uri = mappingId
    ? `mappings/${mappingId}/products/export`
    : `products/export`;
  const response = await apiRequest("POST", uri, dto);
  return await downloadResponseAsFile(response);
}

export async function downloadFile(uri) {
  const response = await apiRequest("GET", uri);
  return await downloadResponseAsFile(response);
}

export const getImportMappings = async (importId) =>
  await jsonGet(`imports/${importId}/mappings`);

export const getImports = async () => await jsonGet(`imports`);

// REMOVE::
export const getImportsAsin = async () => await jsonGet(`imports/asin`);
export const getImportsForeign = async () => await jsonGet(`imports/foreign`);
export const getImportsDomestic = async () => await jsonGet(`imports/domestic`);

export const getImport = async (id) => await jsonGet(`imports/${id}`);

export const deleteImport = async (id) => await jsonDelete(`imports/${id}`);

export const setImportSourceMarketplace = async (importId, marketplaceId) =>
  await jsonPost(`imports/${importId}/source-marketplace`, { marketplaceId });

export const setImportKind = async (importId, kind, marketplaceId = null) =>
  await jsonPost(`imports/${importId}/kind`, { kind, marketplaceId });

export const confirmAsinScrape = async (importId) =>
  await jsonPost(`imports/${importId}/asin-scrape`, {});

export const getSystemMappings = async () => await jsonGet(`mappings/system`);

export const createMapping = async (dto) => await jsonPost(`mappings`, dto);
export const getMappingProfile = async (id) => await jsonGet(`mappings/${id}`);

export const applyMapping = async (importId, mappingId) =>
  await jsonPost(`imports/${importId}/mapping/${mappingId}/products`);

export const optiviseProtoBatch = async (importId, dto) =>
  await jsonPost(`imports/${importId}/optivise`, dto);

export const optiviseProducts = async (mappingId, dto = null) =>
  await jsonPost(`mappings/${mappingId}/products/optivise`, dto);

export const retryFailedOptivise = async (mappingId) =>
  await jsonPost(`mappings/${mappingId}/optivise`);

export const deleteProducts = async (dto) =>
  await jsonPost(`products/delete`, dto);

export const getJobStatus = async (jobId) => await jsonGet(`jobs/${jobId}`);

export const cancelJob = async (jobId) => await jsonPost(`jobs/${jobId}/cancel`);

export const createSuggestion = async (productId, model) =>
  await jsonPost(`products/${productId}/suggestion`, { model });

export const getValueList = async (valueListId) =>
  await jsonGet(`value-lists/${valueListId}`);

export const getValueListByCode = async (marketplaceId, listCode) =>
  await jsonGet(`marketplaces/${marketplaceId}/lists/code/${listCode}`);

export const searchValueListByCode = async (
  marketplaceId,
  listCode,
  query = "",
  limit = 50
) =>
  await jsonGet(
    `marketplaces/${marketplaceId}/lists/code/${listCode}/search?q=${encodeURIComponent(
      query
    )}&limit=${limit}`
  );

export const updateValueList = async (marketplaceId, dto) =>
  await jsonPut(`marketplaces/${marketplaceId}/lists/${dto.id}`, dto);

// PROFILE
export const getProfile = async () => await jsonGet(`profile`);

export const updateProfile = async (dto) => {
  // mixpanel.track("Update profile");
  return await jsonPut(`profile`, dto);
};
// Timezones
export const getTimezones = async () => await jsonGet("timezones");

export const adminImpersonateUser = async (dto) =>
  await jsonPost(`auth/account/impersonate`, dto);

// TENANTS
export const createTenant = async (dto) => await jsonPost(`tenants`, dto);
export const updateTenant = async (id, dto) =>
  await jsonPut(`tenants/${id}`, dto);
export const getTenants = async (page, pageSize) =>
  await jsonGet(`tenants?page=${page}&pageSize=${pageSize}`);
export const getTenant = async (id) => await jsonGet(`tenants/${id}`);

// CREDITS
export const modifyCredits = async (tenantId, dto) => {
  // mix.track("Modify credits", { tenantId });
  return await jsonPost(`tenants/${tenantId}/credits`, dto);
};

export const getAdminJobDetails = async (id) =>
  await jsonGet(`admin-jobs/${id}`);
export const requeueJob = async (id) =>
  await jsonPost(`admin-jobs/${id}/retry`);
export const requeueJobHard = async (id) =>
  await jsonPost(`admin-jobs/${id}/retry-hard`);

export const getTenantProfile = async () => await jsonGet(`tenant/profile`);
export const updateTenantProfile = async (dto) =>
  await jsonPut(`tenant/profile`, dto);

// CHECKOUT & PAYMENTS
// export async function getProducts() {
//   return await jsonGet(`payment/stripe/products`);
// }
export async function checkoutSubscription(dto) {
  return await jsonPost(`payment/stripe/checkout-plan`, dto);
}
export async function checkoutCredits(dto) {
  return await jsonPost("payment/stripe/checkout-credits", dto);
}
export async function checkoutSkuPack(skuCount) {
  return await jsonPost("payment/sku-packs", { skuCount });
}
export async function customerPortal() {
  // mixpanel.track("Customer Portal");
  return await jsonPost("payment/stripe/portal");
}

export function checkout(plan, callback) {
  checkoutSubscription(plan)
    .then((data) => {
      try {
        mixpanel.track("Checkout Plan", {
          plan: plan.planName,
          interval: plan.interval,
        });
      } catch {}
      if (callback) callback();
      location.href = data.redirectUrl;
    })
    .catch((error) => {
      alert("Sorry, something went wrong: " + error);
      if (callback) callback();
    });
}

// USERS
export const getUsers = async (page, pageSize) =>
  await jsonGet(`users?page=${page}&pageSize=${pageSize}`);
export const getSingleUser = async (id) => await jsonGet(`users/${id}`);
export const createUser = async (dto) => await jsonPost(`auth/account`, dto);
export const updateUser = async (id, dto) => await jsonPut(`users/${id}`, dto);
export const deleteUser = async (id) => await jsonDelete(`users/${id}`);

// SIGNUP CODES
export const getSignupCodes = async () =>
  await jsonGet(`admin/signup-codes`);
export const getSignupCode = async (id) =>
  await jsonGet(`admin/signup-codes/${id}`);
export const createSignupCode = async (dto) =>
  await jsonPost(`admin/signup-codes`, dto);
export const updateSignupCode = async (id, dto) =>
  await jsonPut(`admin/signup-codes/${id}`, dto);
export const getSignupCodeRedemptions = async (id) =>
  await jsonGet(`admin/signup-codes/${id}/redemptions`);

export const getPartnerMe = async () => await jsonGet(`partner/me`);
export const getPartnerCodes = async () => await jsonGet(`partner/codes`);
export const getPartnerCode = async (id) => await jsonGet(`partner/codes/${id}`);
export const createPartnerCode = async (dto) =>
  await jsonPost(`partner/codes`, dto);
export const updatePartnerCode = async (id, dto) =>
  await jsonPut(`partner/codes/${id}`, dto);
export const getPartnerCodeRedemptions = async (id) =>
  await jsonGet(`partner/codes/${id}/redemptions`);
export const getPartnerMerchants = async () =>
  await jsonGet(`partner/merchants`);
export const createPartnerMerchant = async (dto) =>
  await jsonPost(`partner/merchants`, dto);
export const assignPartnerMerchantCredits = async (tenantId, skuCount) =>
  await jsonPost(`partner/merchants/${tenantId}/credits`, { skuCount });
export const getAdminPartners = async () => await jsonGet(`admin/partners`);
export const createAdminPartner = async (dto) =>
  await jsonPost(`admin/partners`, dto);
export const updateAdminPartnerSkuOffer = async (tenantId, dto) =>
  await jsonPut(`admin/partners/${tenantId}/sku-offer`, dto);
export const updateTenantSkuOffer = async (tenantId, dto) =>
  await jsonPut(`admin/tenants/${tenantId}/sku-offer`, dto);

// INVITATIONS
export const sendUserInvitation = async (id) =>
  await jsonPost(`users/${id}/invitations`);

export async function uploadImage(file, imageType) {
  let authHeaders = getAuthHeaders();
  var response = await fetch(`${prefix}/profile/images/${imageType}`, {
    method: "POST",
    body: file,
    headers: {
      ...authHeaders,
      "content-type": file.type,
      "content-length": `${file.size}`,
    },
  });
  // mixpanel.track("Upload image", { imageType });
  return await response.json();
}

export const getEsskaTranslationRequest = async (id) =>
  await jsonGet(`esska/translation-requests/${id}`);

export const getEsskaTranslationRequests = async (page, pageSize) =>
  await jsonGet(`esska/translation-requests?page=${page}&pageSize=${pageSize}`);

// Esska Products API
export const getEsskaProducts = async (page, pageSize, categoryId, search, missingTranslation) => {
  let url = `esska/products?page=${page}&pageSize=${pageSize}`;
  if (categoryId) url += `&categoryId=${categoryId}`;
  if (search) url += `&search=${encodeURIComponent(search)}`;
  if (missingTranslation) url += `&missingTranslation=${encodeURIComponent(missingTranslation)}`;
  return await jsonGet(url);
};

export const getEsskaProduct = async (id) =>
  await jsonGet(`esska/products/${id}`);

export const getEsskaProductByJsonId = async (jsonId) =>
  await jsonGet(`esska/products/by-jsonid/${jsonId}`);

export const getEsskaProductVariants = async (id) =>
  await jsonGet(`esska/products/${id}/variants`);

export const getEsskaProductVariantsByJsonId = async (jsonId) =>
  await jsonGet(`esska/products/by-jsonid/${jsonId}/variants`);

export const retranslateProduct = async (
  productJsonId,
  targetLanguage,
  sourceLanguage = "de"
) =>
  await jsonPost(
    `esska/translations/retranslate/${productJsonId}?targetLanguage=${targetLanguage}&sourceLanguage=${sourceLanguage}`
  );

// ============================================================================
// PIM API Functions
// ============================================================================

export const getPimProducts = async (page, pageSize, filters = {}) => {
  const params = new URLSearchParams({
    page: page.toString(),
    pageSize: pageSize.toString(),
  });

  if (filters.sortBy) params.append("sortBy", filters.sortBy);
  if (filters.sortDirection)
    params.append("sortDirection", filters.sortDirection);
  if (filters.filter) params.append("search", filters.filter);
  if (filters.sourceSystem) params.append("sourceSystem", filters.sourceSystem);
  if (filters.status) params.append("status", filters.status);
  if (filters.categoryId) params.append("categoryId", filters.categoryId);

  return await jsonGet(`pim/products?${params.toString()}`);
};

export const getPimProduct = async (id) => await jsonGet(`pim/products/${id}`);

export const updatePimProduct = async (id, data) =>
  await jsonPut(`pim/products/${id}`, data);

export const deletePimProduct = async (id) =>
  await jsonDelete(`pim/products/${id}`);

export const bulkDeletePimProducts = async (ids) =>
  await jsonPost("pim/products/bulk-delete", { ids });

export const getPimVariants = async (productId, page = 1, pageSize = 50) => {
  // const params = new URLSearchParams({
  //   productId,
  //   page: page.toString(),
  //   pageSize: pageSize.toString(),
  // });
  // return await jsonGet(`pim/variants?${params.toString()}`);
  return await jsonGet(`pim/products/${productId}/variants`);
};

export const getPimVariant = async (id) => await jsonGet(`pim/variants/${id}`);

export const updatePimVariant = async (id, data) =>
  await jsonPut(`pim/variants/${id}`, data);

export const getPimAttributes = async (entityType, entityId) => {
  const params = new URLSearchParams({
    entityType,
    entityId,
  });
  return await jsonGet(`pim/attributes?${params.toString()}`);
};

export const getPimAttribute = async (id) =>
  await jsonGet(`pim/attributes/${id}`);

export const updatePimAttribute = async (id, values) =>
  await jsonPut(`pim/attributes/${id}`, { values });

export const createPimAttribute = async (data) =>
  await jsonPost("pim/attributes", data);

export const deletePimAttribute = async (id) =>
  await jsonDelete(`pim/attributes/${id}`);

export const getPimAssets = async (entityType, entityId) => {
  const params = new URLSearchParams({
    entityType,
    entityId,
  });
  return await jsonGet(`pim/assets?${params.toString()}`);
};

export const getPimAsset = async (id) => await jsonGet(`pim/assets/${id}`);

export const updatePimAsset = async (id, data) =>
  await jsonPut(`pim/assets/${id}`, data);

export const createPimAsset = async (data) =>
  await jsonPost("pim/assets", data);

export const deletePimAsset = async (id) =>
  await jsonDelete(`pim/assets/${id}`);
